import { loadRun } from '@/core/save';
import { t } from '@/i18n';
import type { ConfirmDialog } from '@/ui/confirm-dialog';
import { buildHomeArt, homeMessageKey, type HomeMode } from '@/ui/home-art';
import type { SettingsScreen } from '@/ui/settings-screen';
import type { TitleScreen } from '@/ui/title-screen';
import type { UpgradeScreen } from '@/ui/upgrade-screen';
import { music } from '@/audio/music';
import type { RunState } from './run-state';
import type { ScreenSize } from './shop-flow';

/** Overlays the home action drives. */
export interface HomeScreens {
  readonly settings: SettingsScreen;
  readonly confirm: ConfirmDialog;
  readonly shop: UpgradeScreen;
  readonly title: TitleScreen;
}

/**
 * PAUSE → MAIN MENU (polish 1; save rules docs/01, controls docs/06). No new save rule:
 * - at the SHOP BREAK the run is saved exactly like the break's autosave
 *   (CONTINUE reopens this shop; the death bay keeps its X11 checkpoint);
 * - MID-FIGHT nothing is written — the last autosave stands, so CONTINUE
 *   goes back to the last shop break (endless: the last death checkpoint)
 *   and the fight in progress is lost; before the first break of a sector
 *   there is no save at all and the run simply ends.
 * The icon-first ✓ / ✕ confirm shows which of the three applies.
 */
export class HomeFlow {
  constructor(
    private readonly run: RunState,
    private readonly screens: HomeScreens,
    private readonly view: ScreenSize,
    private readonly save: () => void,
  ) {}

  /** A run is live and leaving can't skip a won finale's victory screen. */
  get available(): boolean {
    return !this.screens.title.open && this.run.victoryTimer <= 0;
  }

  /** Opens the pause (settings) screen, with ⌂ MAIN MENU while a run is live. */
  openPause(): void {
    const { width, height } = this.view;
    this.screens.settings.show(width, height, this.available ? () => this.ask() : null);
  }

  private mode(): HomeMode {
    if (this.screens.shop.open) return 'saved';
    return loadRun() ? 'rewind' : 'lost';
  }

  /** ✓ / ✕ confirm: house + what happens to the run. */
  private ask(): void {
    const mode = this.mode();
    // A saved run defaults to ✓ (nothing to lose); anything lost to ✕
    this.screens.confirm.show(
      this.view.width, this.view.height, t(homeMessageKey(mode)), () => this.go(),
      mode === 'saved', { art: () => buildHomeArt(mode), glyphs: true },
    );
  }

  private go(): void {
    const { run, screens } = this;
    if (screens.shop.open) {
      if (run.deathShop) {
        // Death bay: same as the burn picker's MAIN MENU — a checkpoint save
        run.deathShop = false;
        run.nextPick = null;
        screens.shop.hide();
        this.save();
      } else {
        this.save(); // the shop-break save (resumeAt 'shop')
        screens.shop.hide();
      }
    }
    screens.settings.hide();
    music.playShop(); // the calm loop the title gets after any run
    screens.title.show(this.view.width, this.view.height);
  }
}
